import type { JiraIssue } from '../api/JiraApi';

/** Sentinel for issues with no assignee (multi-select value). */
export const JIRA_FILTER_UNASSIGNED = '__UNASSIGNED__';

/** Sentinel for issues with no Jira labels (multi-select value). */
export const JIRA_FILTER_NO_LABELS = '__NO_LABELS__';

export function isTerminalDoneOrClosed(issue: JiraIssue): boolean {
  if (issue.statusCategory === 'done') {
    return true;
  }
  const s = issue.status.trim().toLowerCase();
  return s === 'done' || s === 'closed';
}

export interface JiraIssueFilters {
  /** When false (default), issues that are Done/Closed are excluded first. */
  showDoneClosed: boolean;
  /** If empty, any type passes (after done/closed rule). */
  types: string[];
  /** If empty, any assignee passes. Use {@link JIRA_FILTER_UNASSIGNED} for unassigned. */
  assignees: string[];
  /** If empty, any status passes. */
  statuses: string[];
  /**
   * If empty, any labels pass. Otherwise issue matches if it has **any** selected
   * label (OR). Use {@link JIRA_FILTER_NO_LABELS} for issues with no labels.
   */
  labels: string[];
}

export function filterJiraIssues(
  issues: JiraIssue[],
  filters: JiraIssueFilters,
): JiraIssue[] {
  let list = issues;
  if (!filters.showDoneClosed) {
    list = list.filter(i => !isTerminalDoneOrClosed(i));
  }
  if (filters.types.length) {
    list = list.filter(i => filters.types.includes(i.type));
  }
  if (filters.assignees.length) {
    list = list.filter(i => {
      const key = i.assignee ?? JIRA_FILTER_UNASSIGNED;
      return filters.assignees.includes(key);
    });
  }
  if (filters.statuses.length) {
    list = list.filter(i => filters.statuses.includes(i.status));
  }
  if (filters.labels.length) {
    list = list.filter(i => {
      const issueLabels = i.labels?.filter(Boolean) ?? [];
      return filters.labels.some(f => {
        if (f === JIRA_FILTER_NO_LABELS) {
          return issueLabels.length === 0;
        }
        return issueLabels.includes(f);
      });
    });
  }
  return list;
}

export function distinctTypes(issues: JiraIssue[]): string[] {
  return [...new Set(issues.map(i => i.type))].sort((a, b) =>
    a.localeCompare(b),
  );
}

export function distinctStatuses(issues: JiraIssue[]): string[] {
  return [...new Set(issues.map(i => i.status))].sort((a, b) =>
    a.localeCompare(b),
  );
}

/** Distinct assignee keys (uses {@link JIRA_FILTER_UNASSIGNED} when missing). */
export function distinctAssigneeKeys(issues: JiraIssue[]): string[] {
  return [
    ...new Set(issues.map(i => i.assignee ?? JIRA_FILTER_UNASSIGNED)),
  ].sort((a, b) => {
    if (a === JIRA_FILTER_UNASSIGNED) return -1;
    if (b === JIRA_FILTER_UNASSIGNED) return 1;
    return a.localeCompare(b);
  });
}

/** Distinct label names across issues, plus {@link JIRA_FILTER_NO_LABELS} when any issue has no labels. */
export function distinctLabelKeys(issues: JiraIssue[]): string[] {
  const names = new Set<string>();
  let hasUnlabeled = false;
  for (const i of issues) {
    const ls = i.labels?.filter(Boolean) ?? [];
    if (ls.length === 0) {
      hasUnlabeled = true;
    }
    ls.forEach(l => names.add(l));
  }
  const sorted = [...names].sort((a, b) => a.localeCompare(b));
  if (hasUnlabeled) {
    return [JIRA_FILTER_NO_LABELS, ...sorted];
  }
  return sorted;
}
